<script setup>
import { onMounted, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useRoute, useRouter } from 'vue-router';
import { useDealsStore } from 'dashboard/stores/deals';
import { formatDistanceToNow, parseISO, isValid } from 'date-fns';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import ActivityTimeline from 'dashboard/components-next/Activities/ActivityTimeline/ActivityTimeline.vue';

const route = useRoute();
const router = useRouter();
const dealsStore = useDealsStore();
const { t } = useI18n();

const isEditing = ref(false);
const isSaving = ref(false);
const activeTab = ref('activities');
const hasError = ref(false);

const dealId = computed(() => parseInt(route.params.dealId));
const uiFlags = computed(() => dealsStore.getUIFlags);
const selectedDeal = computed(() => dealsStore.getSelectedDeal);

const isFetchingItem = computed(() => uiFlags.value.fetchingItem);
const isUpdating = computed(() => uiFlags.value.updating);

// Local editable copy of deal data
const dealData = ref({});

const DEAL_TABS_OPTIONS = [
  { key: 'ACTIVITIES', value: 'activities' },
  { key: 'TASKS', value: 'tasks' },
];

const tabs = computed(() => {
  return DEAL_TABS_OPTIONS.map(tab => ({
    label: t(`ACTIVITIES.TABS.${tab.key}`),
    value: tab.value,
  }));
});

const activeTabIndex = computed(() => {
  return DEAL_TABS_OPTIONS.findIndex(v => v.value === activeTab.value);
});

const stageOptions = [
  { value: 'lead', label: t('DEALS.STAGES.LEAD') },
  { value: 'qualified', label: t('DEALS.STAGES.QUALIFIED') },
  { value: 'proposal', label: t('DEALS.STAGES.PROPOSAL') },
  { value: 'negotiation', label: t('DEALS.STAGES.NEGOTIATION') },
  { value: 'closed_won', label: t('DEALS.STAGES.CLOSED_WON') },
  { value: 'closed_lost', label: t('DEALS.STAGES.CLOSED_LOST') },
];

const currencyOptions = [
  { value: 'USD', label: '$ USD' },
  { value: 'EUR', label: '€ EUR' },
  { value: 'GBP', label: '£ GBP' },
  { value: 'JPY', label: '¥ JPY' },
  { value: 'CAD', label: 'C$ CAD' },
  { value: 'AUD', label: 'A$ AUD' },
  { value: 'NGN', label: '₦ NGN' },
];

const selectedStageLabel = computed(() => {
  const stage = stageOptions.find(s => s.value === dealData.value?.stage);
  return stage?.label || t('DEALS.STAGES.LEAD');
});

const breadcrumbItems = computed(() => {
  const items = [
    {
      label: t('DEALS.TITLE'),
      link: '#',
    },
  ];
  if (selectedDeal.value) {
    items.push({
      label: selectedDeal.value.title || t('DEALS.UNTITLED'),
    });
  }
  return items;
});

const formattedValue = computed(() => {
  if (!dealData.value?.value) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: dealData.value?.currency || 'USD',
    minimumFractionDigits: 0,
  }).format(dealData.value.value);
});

const stageColorClass = computed(() => {
  if (!dealData.value?.stage) return '';
  const colors = {
    lead: 'bg-n-slate-9/10 text-n-slate-11',
    qualified: 'bg-n-blue-9/10 text-n-blue-11',
    proposal: 'bg-n-amber-9/10 text-n-amber-11',
    negotiation: 'bg-n-purple-9/10 text-n-purple-11',
    closed_won: 'bg-n-teal-9/10 text-n-teal-11',
    closed_lost: 'bg-n-ruby-9/10 text-n-ruby-11',
  };
  return colors[dealData.value.stage] || colors.lead;
});

const stageIcon = computed(() => {
  const icons = {
    lead: 'i-lucide-user',
    qualified: 'i-lucide-check-circle',
    proposal: 'i-lucide-file-text',
    negotiation: 'i-lucide-handshake',
    closed_won: 'i-lucide-trophy',
    closed_lost: 'i-lucide-x-circle',
  };
  return icons[dealData.value?.stage] || icons.lead;
});

// Safely format date
const formatDate = (dateValue) => {
  if (!dateValue) return '';
  try {
    const date = typeof dateValue === 'string' ? parseISO(dateValue) : new Date(dateValue);
    if (!isValid(date)) return '';
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (e) {
    return '';
  }
};

const updatedAt = computed(() => {
  return formatDate(dealData.value?.updatedAt);
});

// Initialize local data from store
const initializeData = () => {
  if (selectedDeal.value && typeof selectedDeal.value === 'object') {
    dealData.value = { ...selectedDeal.value };
  } else {
    dealData.value = {};
  }
};

watch(() => selectedDeal.value, initializeData, { immediate: true });

const goToDealsList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/crm/deals`);
  }
};

const handleBreadcrumbClick = () => {
  goToDealsList();
};

const fetchDeal = async () => {
  if (!dealId.value) return;
  hasError.value = false;
  try {
    await dealsStore.show(dealId.value);
  } catch (error) {
    hasError.value = true;
    console.error('Failed to fetch deal:', error);
    useAlert(t('DEALS.FETCH_ERROR'));
  }
};

const handleUpdateDeal = async () => {
  if (!dealData.value) return;

  isSaving.value = true;
  try {
    await dealsStore.update(dealId.value, {
      title: dealData.value.title,
      value: dealData.value.value,
      currency: dealData.value.currency,
      stage: dealData.value.stage,
      probability: dealData.value.probability,
      expected_close_date: dealData.value.expectedCloseDate,
      description: dealData.value.description,
    });
    useAlert(t('DEALS.UPDATE_SUCCESS'));
    isEditing.value = false;
  } catch (error) {
    useAlert(t('DEALS.UPDATE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const handleDeleteDeal = async () => {
  if (!confirm(t('DEALS.DELETE_CONFIRM'))) return;

  try {
    await dealsStore.delete(dealId.value);
    useAlert(t('DEALS.DELETE_SUCCESS'));
    goToDealsList();
  } catch (error) {
    useAlert(t('DEALS.DELETE_ERROR'));
  }
};

const handleTabChange = tab => {
  activeTab.value = tab.value;
};

const startEditing = () => {
  initializeData();
  isEditing.value = true;
};

const cancelEditing = () => {
  initializeData();
  isEditing.value = false;
};

// Refetch when dealId changes
watch(dealId, () => {
  fetchDeal();
});

onMounted(() => {
  fetchDeal();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <!-- Loading State -->
    <div
      v-if="isFetchingItem"
      class="flex items-center justify-center flex-1 h-full"
    >
      <Spinner />
    </div>

    <!-- Error State -->
    <div
      v-else-if="hasError"
      class="flex flex-col items-center justify-center flex-1 h-full gap-4"
    >
      <span class="i-lucide-alert-circle text-n-ruby-9 size-12" />
      <p class="text-n-slate-11">{{ t('DEALS.FETCH_ERROR') }}</p>
      <Button :label="t('DEALS.RETRY')" @click="fetchDeal" />
    </div>

    <!-- Content -->
    <section
      v-else-if="selectedDeal"
      class="flex w-full h-full overflow-hidden justify-evenly bg-n-background"
    >
      <!-- Main Content -->
      <div
        class="flex flex-col w-full h-full transition-all duration-300 ltr:2xl:ml-56 rtl:2xl:mr-56"
      >
        <header class="sticky top-0 z-10 px-6 3xl:px-0">
          <div class="w-full mx-auto max-w-[40.625rem]">
            <div
              class="flex flex-col xs:flex-row items-start xs:items-center justify-between w-full py-7 gap-2"
            >
              <Breadcrumb
                :items="breadcrumbItems"
                @click="handleBreadcrumbClick"
              />
              <div class="flex items-center gap-2">
                <template v-if="!isEditing">
                  <Button
                    :label="t('DEALS.EDIT')"
                    size="sm"
                    slate
                    :is-loading="isUpdating"
                    :disabled="isUpdating"
                    @click="startEditing"
                  />
                </template>
                <template v-else>
                  <Button
                    :label="t('DEALS.CANCEL')"
                    size="sm"
                    ghost
                    slate
                    @click="cancelEditing"
                  />
                  <Button
                    :label="t('DEALS.SAVE')"
                    size="sm"
                    :is-loading="isSaving"
                    @click="handleUpdateDeal"
                  />
                </template>
              </div>
            </div>
          </div>
        </header>

        <main class="flex-1 px-6 overflow-y-auto 3xl:px-px">
          <div class="w-full py-4 mx-auto max-w-[40.625rem]">
            <!-- Deal Header -->
            <div class="flex flex-col gap-6 pb-6">
              <!-- Title and Value Row -->
              <div class="flex flex-col gap-4">
                <!-- Read Mode Header -->
                <div v-if="!isEditing" class="flex flex-col gap-3">
                  <div class="flex items-start justify-between gap-4">
                    <div class="flex flex-col gap-2">
                      <h1 class="text-2xl font-semibold text-n-slate-12">
                        {{ dealData.title || t('DEALS.UNTITLED') }}
                      </h1>
                      <div
                        :class="[
                          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium w-fit',
                          stageColorClass,
                        ]"
                      >
                        <span :class="stageIcon" class="size-3.5" />
                        {{ selectedStageLabel }}
                      </div>
                    </div>
                    <div class="text-right">
                      <div class="text-3xl font-bold text-n-slate-12">
                        {{ formattedValue }}
                      </div>
                      <div
                        v-if="dealData.probability"
                        class="text-sm text-n-slate-11"
                      >
                        {{ dealData.probability }}%
                        {{ t('DEALS.PROBABILITY') }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Edit Mode Header -->
                <div v-else class="flex flex-col gap-4">
                  <Input
                    v-model="dealData.title"
                    :label="t('DEALS.FORM.TITLE_LABEL')"
                    :placeholder="t('DEALS.FORM.TITLE_PLACEHOLDER')"
                  />
                  <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div class="col-span-2">
                      <Input
                        v-model="dealData.value"
                        type="number"
                        :label="t('DEALS.FORM.VALUE_LABEL')"
                        :placeholder="t('DEALS.FORM.VALUE_PLACEHOLDER')"
                      />
                    </div>
                    <div class="flex flex-col gap-2">
                      <label class="text-sm font-medium text-n-slate-12">
                        {{ t('DEALS.FORM.CURRENCY_LABEL') }}
                      </label>
                      <ComboBox
                        v-model="dealData.currency"
                        :options="currencyOptions"
                      />
                    </div>
                    <div class="flex flex-col gap-2">
                      <label class="text-sm font-medium text-n-slate-12">
                        {{ t('DEALS.FORM.STAGE_LABEL') }}
                      </label>
                      <ComboBox
                        v-model="dealData.stage"
                        :options="stageOptions"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Key Metrics Grid -->
              <div
                class="grid grid-cols-2 gap-4 p-4 rounded-lg bg-n-alpha-2"
              >
                <!-- Expected Close Date -->
                <div class="flex flex-col gap-1">
                  <span class="text-xs font-medium text-n-slate-11">
                    {{ t('DEALS.FORM.EXPECTED_CLOSE_LABEL') }}
                  </span>
                  <div v-if="!isEditing" class="text-sm text-n-slate-12">
                    {{ dealData.expectedCloseDate || '-' }}
                  </div>
                  <Input
                    v-else
                    v-model="dealData.expectedCloseDate"
                    type="date"
                    size="sm"
                  />
                </div>

                <!-- Probability -->
                <div class="flex flex-col gap-1">
                  <span class="text-xs font-medium text-n-slate-11">
                    {{ t('DEALS.FORM.PROBABILITY_LABEL') }}
                  </span>
                  <div v-if="!isEditing" class="text-sm text-n-slate-12">
                    {{ dealData.probability ? `${dealData.probability}%` : '-' }}
                  </div>
                  <Input
                    v-else
                    v-model="dealData.probability"
                    type="number"
                    min="0"
                    max="100"
                    size="sm"
                    :placeholder="t('DEALS.FORM.PROBABILITY_PLACEHOLDER')"
                  />
                </div>
              </div>

              <!-- Relation Info Cards -->
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <!-- Company -->
                <div
                  class="flex items-center gap-3 p-3 rounded-lg bg-n-alpha-2"
                >
                  <div
                    class="flex items-center justify-center w-10 h-10 rounded-lg bg-n-slate-3"
                  >
                    <span class="i-lucide-building-2 text-n-slate-11 size-5" />
                  </div>
                  <div class="flex flex-col min-w-0">
                    <span class="text-xs text-n-slate-11">
                      {{ t('DEALS.FORM.COMPANY_LABEL') }}
                    </span>
                    <router-link
                      v-if="dealData.company?.id"
                      :to="`/app/accounts/${route.params.accountId}/companies/${dealData.company.id}`"
                      class="text-sm font-medium text-n-slate-12 truncate hover:text-n-brand transition-colors"
                    >
                      {{ dealData.company.name }}
                    </router-link>
                    <span v-else class="text-sm text-n-slate-12">
                      {{ t('DEALS.NO_COMPANY') }}
                    </span>
                  </div>
                </div>

                <!-- Contact -->
                <div
                  class="flex items-center gap-3 p-3 rounded-lg bg-n-alpha-2"
                >
                  <Avatar
                    :src="dealData.contact?.avatarUrl"
                    :name="dealData.contact?.name || ''"
                    :size="40"
                    rounded-full
                  />
                  <div class="flex flex-col min-w-0">
                    <span class="text-xs text-n-slate-11">
                      {{ t('DEALS.FORM.CONTACT_LABEL') }}
                    </span>
                    <router-link
                      v-if="dealData.contact?.id"
                      :to="`/app/accounts/${route.params.accountId}/contacts/${dealData.contact.id}`"
                      class="text-sm font-medium text-n-slate-12 truncate hover:text-n-brand transition-colors"
                    >
                      {{ dealData.contact.name }}
                    </router-link>
                    <span v-else class="text-sm text-n-slate-12"> - </span>
                  </div>
                </div>

                <!-- Assigned To -->
                <div
                  class="flex items-center gap-3 p-3 rounded-lg bg-n-alpha-2"
                >
                  <Avatar
                    :src="dealData.user?.avatarUrl"
                    :name="dealData.user?.name || ''"
                    :size="40"
                    rounded-full
                  />
                  <div class="flex flex-col min-w-0">
                    <span class="text-xs text-n-slate-11">
                      {{ t('DEALS.FORM.ASSIGNEE_LABEL') }}
                    </span>
                    <span class="text-sm font-medium text-n-slate-12 truncate">
                      {{ dealData.user?.name || '-' }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Description -->
              <div class="flex flex-col gap-2">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('DEALS.FORM.DESCRIPTION_LABEL') }}
                </span>
                <p
                  v-if="!isEditing"
                  class="text-sm text-n-slate-11 whitespace-pre-wrap"
                >
                  {{ dealData.description || '-' }}
                </p>
                <textarea
                  v-else
                  v-model="dealData.description"
                  rows="4"
                  class="w-full px-3 py-2.5 text-sm bg-n-alpha-black2 text-n-slate-12 rounded-lg outline outline-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand transition-all resize-none"
                  :placeholder="t('DEALS.FORM.DESCRIPTION_PLACEHOLDER')"
                />
              </div>

              <!-- Last Updated -->
              <div v-if="updatedAt" class="flex items-center gap-1.5 text-xs text-n-slate-11">
                <span class="i-lucide-clock size-3.5" />
                {{ t('DEALS.UPDATED_AT', { date: updatedAt }) }}
              </div>
            </div>

            <!-- Danger Zone - Delete -->
            <div
              class="flex flex-col items-start w-full gap-4 pt-6 border-t border-n-strong"
            >
              <div class="flex flex-col gap-2">
                <h6 class="text-base font-medium text-n-slate-12">
                  {{ t('DEALS.DELETE') }}
                </h6>
                <span class="text-sm text-n-slate-11">
                  {{ t('DEALS.DELETE_DESCRIPTION') }}
                </span>
              </div>
              <Button
                :label="t('DEALS.DELETE')"
                color="ruby"
                @click="handleDeleteDeal"
              />
            </div>
          </div>
        </main>
      </div>

      <!-- Sidebar -->
      <div
        class="hidden lg:block overflow-y-auto justify-end min-w-52 w-full py-6 max-w-md border-l border-n-weak bg-n-solid-2"
      >
        <div class="px-6">
          <TabBar
            :tabs="tabs"
            :initial-active-tab="activeTabIndex"
            class="w-full [&>button]:w-full bg-n-alpha-black2"
            @tab-changed="handleTabChange"
          />
        </div>

        <!-- Activities Tab -->
        <div v-if="activeTab === 'activities'" class="mt-4 h-[calc(100%-3rem)]">
          <ActivityTimeline
            activitable-type="Deal"
            :activitable-id="dealId"
          />
        </div>

        <!-- Tasks Tab -->
        <div v-if="activeTab === 'tasks'" class="px-6 mt-4">
          <div class="flex flex-col gap-4">
            <div
              class="flex items-center justify-center py-8 text-sm text-n-slate-11"
            >
              {{ t('TASKS.EMPTY') }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Not Found State -->
    <div
      v-else
      class="flex flex-col items-center justify-center flex-1 h-full gap-4"
    >
      <span class="i-lucide-briefcase text-n-slate-9 size-12" />
      <p class="text-n-slate-11">{{ t('DEALS.EMPTY_STATE.LIST_EMPTY_STATE_TITLE') }}</p>
      <Button :label="t('DEALS.BACK')" @click="goToDealsList" />
    </div>
  </div>
</template>
